/**
 * CR-6440/CR-6448: sectioned layout for the Purchasing → Forecast filter panel.
 *
 * For Supplier View:
 *   - Collapsible accordion sections with chevrons (CR-6448)
 *   - Blue dot indicators for sections with active filters
 *   - Sections: Date, Company, Supplier, Classification, Forecast Details
 *
 * For Customer View:
 *   - Flat layout with FilterSection components
 *   - Sections: Date, Company, Supplier, Classification, Forecast Details
 *   - CR-6440: Added cascade filter logic matching PO Headers:
 *     - Division → Site, Company Country → Site
 *     - Site → Supplier, Supplier Country → Supplier, Supplier Group → Supplier
 *     - Commodity Group → Commodity Type
 *
 * Input types are preserved exactly as the ticket requires.
 */
import { useMemo } from 'react';
import SearchableSelect from '../../../ui/SearchableSelect';
import { FilterSection, FilterGrid, FilterField, CollapsibleSection } from '../../../filters/FilterSection';
import { useForecastFilterCascades, ForecastCascadeFilterOptions } from './useForecastFilterCascades';

/** Control styling matching this panel's existing density. */
const inputCls = 'w-full px-3 py-2 border border-border rounded-md text-sm focus:ring-2 focus:ring-primary bg-background';
const disabledCls = `${inputCls} bg-muted/40 text-muted-foreground cursor-not-allowed`;

interface ForecastFilterOptionsShape {
  commodity_groups?: string[];
  commodity_types?: string[];
  supplier_groups?: Array<{ id?: number; name: string }>;
  suppliers?: Array<{ id: number; name: string }>;
  customers?: Array<{ id: number; name: string }>;
}

export interface ForecastFilterSectionsProps {
  filters: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
  filterOptions?: ForecastFilterOptionsShape | null;
  /** Customer portal — supplier pickers only make sense there. */
  isCustomer: boolean;
  /** Supplier portal — the Customer picker only makes sense there. */
  isSupplier: boolean;
  /** Schedule-tenant installs carry a separate supplier part number. */
  isScheduleTenant: boolean;
  /** Current supplier site name for display in Supplier View. */
  supplierSiteName?: string;
  /** CR-6440: Division options for cascade filtering */
  divisionOptions?: Array<{ value: string; label: string }>;
  /** CR-6440: Site country options for cascade filtering */
  siteCountries?: string[];
  /** CR-6440: Supplier country options for cascade filtering */
  supplierCountries?: string[];
  /** CR-6440: Site options for cascade filtering */
  siteOptions?: Array<{ value: string; label: string }>;
  /**
   * CR-6440: Raw filter data for cascade filtering.
   * When provided, cascade logic filters dependent dropdowns automatically.
   */
  rawCascadeOptions?: ForecastCascadeFilterOptions | null;
}

export function ForecastFilterSections({
  filters,
  onFilterChange,
  filterOptions,
  isCustomer,
  isSupplier,
  isScheduleTenant,
  supplierSiteName,
  divisionOptions = [],
  siteCountries = [],
  supplierCountries = [],
  siteOptions: propSiteOptions = [],
  rawCascadeOptions,
}: ForecastFilterSectionsProps) {
  // CR-6440: Build filters object for cascade hook (cast from Record<string, string>)
  const typedFilters = {
    partNumber: filters['partNumber'] || '',
    poNumber: filters['poNumber'] || '',
    supplierPartNumber: filters['supplierPartNumber'] || '',
    partSearch: filters['partSearch'] || '',
    commodityGroup: filters['commodityGroup'] || '',
    commodityType: filters['commodityType'] || '',
    supplierGroup: filters['supplierGroup'] || '',
    supplierId: filters['supplierId'] || '',
    customerId: filters['customerId'] || '',
    recordType: filters['recordType'] || '',
    importDateFrom: filters['importDateFrom'] || '',
    importDateTo: filters['importDateTo'] || '',
    dateFrom: filters['dateFrom'] || '',
    dateTo: filters['dateTo'] || '',
    division: filters['division'] || '',
    companyCountry: filters['companyCountry'] || '',
    site: filters['site'] || '',
    supplierCountry: filters['supplierCountry'] || '',
  };

  // CR-6440: Use cascade hook for dependent filter logic
  const {
    siteOptions: cascadedSiteOptions,
    supplierSiteOptions: cascadedSupplierOptions,
    commodityTypeOptions: cascadedCommodityTypes,
    handleDivisionChange,
    handleCompanyCountryChange,
    handleSiteChange,
    handleSupplierCountryChange,
    handleSupplierGroupChange,
    handleCommodityGroupChange,
  } = useForecastFilterCascades(typedFilters, rawCascadeOptions ?? null, onFilterChange);

  // CR-6440: Use cascaded options when raw data is available,
  // otherwise fall back to props for backward compatibility.
  const effectiveSiteOptions = useMemo(() => {
    if (rawCascadeOptions && rawCascadeOptions.sites.length > 0) {
      return cascadedSiteOptions.map((s) => ({ value: String(s.id), label: s.name }));
    }
    return propSiteOptions;
  }, [rawCascadeOptions, cascadedSiteOptions, propSiteOptions]);

  const effectiveSupplierOptions = useMemo(() => {
    if (rawCascadeOptions && rawCascadeOptions.suppliers.length > 0) {
      return cascadedSupplierOptions.map((s) => ({ value: String(s.id), label: s.name }));
    }
    return (filterOptions?.suppliers || []).map((s) => ({ value: String(s.id), label: s.name }));
  }, [rawCascadeOptions, cascadedSupplierOptions, filterOptions?.suppliers]);

  const effectiveCommodityTypes = useMemo(() => {
    if (rawCascadeOptions && rawCascadeOptions.commodityTypes.length > 0) {
      return cascadedCommodityTypes.map((t) => t.name);
    }
    return filterOptions?.commodity_types || [];
  }, [rawCascadeOptions, cascadedCommodityTypes, filterOptions?.commodity_types]);

  // Check which sections have active filters for the blue dot indicator
  const hasDateFilters = !!filters['dateFrom'] || !!filters['dateTo'];
  const hasCompanyFilters = !!filters['customerId'] || !!filters['site'] || !!filters['division'] || !!filters['companyCountry'];
  const hasSupplierFilters = !!filters['supplierGroup'] || !!filters['supplierId'] || !!filters['supplierCountry'];
  const hasClassificationFilters = !!filters['commodityGroup'] || !!filters['commodityType'];
  const hasForecastFilters = !!filters['partNumber'] || !!filters['poNumber'] ||
    !!filters['supplierPartNumber'] || !!filters['recordType'];

  const textField = (label: string, key: string, placeholder: string) => (
    <FilterField label={label}>
      <input
        type="text"
        value={filters[key] || ''}
        onChange={(e) => onFilterChange(key, e.target.value)}
        placeholder={placeholder}
        className={inputCls}
      />
    </FilterField>
  );

  // CR-6448: Supplier View gets collapsible accordion design matching diagram
  // All existing filters are preserved per client requirement
  if (isSupplier) {
    return (
      <div className="space-y-0">
        {/* DATE section - collapsible */}
        <CollapsibleSection title="Date" isActive={hasDateFilters}>
          <FilterGrid>
            <FilterField label="Date From">
              <input
                type="date"
                value={filters['dateFrom'] || ''}
                max={filters['dateTo'] || undefined}
                onChange={(e) => onFilterChange('dateFrom', e.target.value)}
                className={inputCls}
              />
            </FilterField>
            <FilterField label="Date To">
              <input
                type="date"
                value={filters['dateTo'] || ''}
                min={filters['dateFrom'] || undefined}
                onChange={(e) => onFilterChange('dateTo', e.target.value)}
                className={inputCls}
              />
            </FilterField>
          </FilterGrid>
        </CollapsibleSection>

        {/* COMPANY section - collapsible (Site/Warehouse per diagram + existing Customer filter) */}
        <CollapsibleSection title="Company" isActive={hasCompanyFilters}>
          <FilterGrid>
            <FilterField label="Customer">
              <select
                value={filters['customerId'] || ''}
                onChange={(e) => onFilterChange('customerId', e.target.value)}
                className={inputCls}
              >
                <option value="">All Customers</option>
                {(filterOptions?.customers || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="Site">
              <select
                disabled
                className={disabledCls}
                title="Coming soon"
              >
                <option value="">All sites</option>
              </select>
            </FilterField>
            <FilterField label="Warehouse (if enabled)">
              <select
                disabled
                className={disabledCls}
                title="Coming soon"
              >
                <option value="">All warehouses</option>
              </select>
            </FilterField>
          </FilterGrid>
        </CollapsibleSection>

        {/* SUPPLIER section - collapsible (per CR-6448 diagram) */}
        <CollapsibleSection title="Supplier" isActive={hasSupplierFilters}>
          <FilterGrid>
            <FilterField label="Group">
              <SearchableSelect
                options={(filterOptions?.supplier_groups || []).map((g) => ({
                  value: g.name,
                  label: g.name,
                }))}
                value={filters['supplierGroup'] || ''}
                onChange={(val) => onFilterChange('supplierGroup', val)}
                placeholder="All supplier groups"
              />
            </FilterField>
            <FilterField label="Site">
              <input
                type="text"
                value={supplierSiteName || ''}
                disabled
                className={disabledCls}
                title="Locked to your supplier site"
              />
            </FilterField>
          </FilterGrid>
          <p className="text-xs text-muted-foreground mt-2 italic">
            Locked to your supplier site
          </p>
        </CollapsibleSection>

        {/* CLASSIFICATION section - collapsible (from legacy platform) */}
        <CollapsibleSection title="Classification" isActive={hasClassificationFilters}>
          <FilterGrid>
            <FilterField label="Commodity Group">
              <select
                value={filters['commodityGroup'] || ''}
                onChange={(e) => onFilterChange('commodityGroup', e.target.value)}
                className={inputCls}
              >
                <option value="">All Groups</option>
                {(filterOptions?.commodity_groups || []).map((group) => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="Commodity Type">
              <select
                value={filters['commodityType'] || ''}
                onChange={(e) => onFilterChange('commodityType', e.target.value)}
                className={inputCls}
              >
                <option value="">All Types</option>
                {(filterOptions?.commodity_types || []).map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </FilterField>
          </FilterGrid>
        </CollapsibleSection>

        {/* FORECAST DETAILS section - collapsible */}
        <CollapsibleSection title="Forecast Details" isActive={hasForecastFilters}>
          <FilterGrid>
            {textField('Part Number', 'partNumber', 'Part number...')}
            {textField('SO Number', 'poNumber', 'Search SO...')}
            {isScheduleTenant && textField('Supplier Part No', 'supplierPartNumber', 'Supplier part...')}
            <FilterField label="Record Type">
              <select
                value={filters['recordType'] || ''}
                onChange={(e) => onFilterChange('recordType', e.target.value)}
                className={inputCls}
              >
                <option value="">All</option>
                <option value="2">Open PO only</option>
                <option value="1">Forecast only</option>
              </select>
            </FilterField>
          </FilterGrid>
        </CollapsibleSection>
      </div>
    );
  }

  // Customer View - flat layout with FilterSection
  return (
    <>
      {/* Date section */}
      <FilterSection title="Date" bare>
        <FilterGrid>
          <FilterField label="Date From">
            <input
              type="date"
              value={filters['dateFrom'] || ''}
              max={filters['dateTo'] || undefined}
              onChange={(e) => onFilterChange('dateFrom', e.target.value)}
              className={inputCls}
            />
          </FilterField>
          <FilterField label="Date To">
            <input
              type="date"
              value={filters['dateTo'] || ''}
              min={filters['dateFrom'] || undefined}
              onChange={(e) => onFilterChange('dateTo', e.target.value)}
              className={inputCls}
            />
          </FilterField>
        </FilterGrid>
      </FilterSection>

      {/* CR-6440: Company section with cascade filtering (Division → Site, Country → Site) */}
      {isCustomer && (
        <FilterSection title="Company" bare>
          <FilterGrid>
            <FilterField label="Country">
              <select
                value={filters['companyCountry'] || ''}
                onChange={(e) => handleCompanyCountryChange(e.target.value)}
                className={inputCls}
              >
                <option value="">All Countries</option>
                {siteCountries.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="Division">
              <select
                value={filters['division'] || ''}
                onChange={(e) => handleDivisionChange(e.target.value)}
                className={inputCls}
              >
                <option value="">All Divisions</option>
                {divisionOptions.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="Site">
              <SearchableSelect
                options={effectiveSiteOptions}
                value={filters['site'] || ''}
                onChange={(val) => handleSiteChange(val)}
                placeholder="All sites"
              />
            </FilterField>
          </FilterGrid>
        </FilterSection>
      )}

      {/* CR-6440: Supplier section with cascade filtering (Site → Supplier, Country → Supplier, Group → Supplier) */}
      {isCustomer && (
        <FilterSection title="Supplier" bare>
          <FilterGrid>
            <FilterField label="Country">
              <select
                value={filters['supplierCountry'] || ''}
                onChange={(e) => handleSupplierCountryChange(e.target.value)}
                className={inputCls}
              >
                <option value="">All Countries</option>
                {supplierCountries.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="Supplier Group">
              <SearchableSelect
                options={(filterOptions?.supplier_groups || []).map((g) => ({ value: g.name, label: g.name }))}
                value={filters['supplierGroup'] || ''}
                onChange={(val) => handleSupplierGroupChange(val)}
                placeholder="All Supplier Groups"
              />
            </FilterField>
            <FilterField label="Supplier Site">
              <SearchableSelect
                options={effectiveSupplierOptions}
                value={filters['supplierId'] || ''}
                onChange={(val) => onFilterChange('supplierId', val)}
                placeholder="All Supplier Sites"
              />
            </FilterField>
          </FilterGrid>
        </FilterSection>
      )}

      {/* Classification section with cascade filtering (CommodityGroup → CommodityType) */}
      <FilterSection title="Classification" bare>
        <FilterGrid>
          <FilterField label="Commodity Group">
            <select
              value={filters['commodityGroup'] || ''}
              onChange={(e) => handleCommodityGroupChange(e.target.value)}
              className={inputCls}
            >
              <option value="">All Groups</option>
              {(filterOptions?.commodity_groups || []).map((group) => (
                <option key={group} value={group}>{group}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Commodity Type">
            <select
              value={filters['commodityType'] || ''}
              onChange={(e) => onFilterChange('commodityType', e.target.value)}
              className={inputCls}
            >
              <option value="">All Types</option>
              {effectiveCommodityTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </FilterField>
        </FilterGrid>
      </FilterSection>

      {/* Forecast Details section */}
      <FilterSection title="Forecast Details" bare>
        <FilterGrid>
          {textField('Part Number', 'partNumber', 'Search part...')}
          {textField('PO Number', 'poNumber', 'Search PO...')}
          {isScheduleTenant && textField('Supplier Part No', 'supplierPartNumber', 'Supplier part...')}
          <FilterField label="Record Type">
            <select
              value={filters['recordType'] || ''}
              onChange={(e) => onFilterChange('recordType', e.target.value)}
              className={inputCls}
            >
              <option value="">All</option>
              <option value="2">Open PO</option>
              <option value="1">Forecast</option>
            </select>
          </FilterField>
        </FilterGrid>
      </FilterSection>
    </>
  );
}
